/**
 * Markdown.tsx — a small, safe markdown renderer for PulseBot replies.
 *
 * Why hand-rolled rather than react-markdown: the assistant's output is
 * constrained by its system prompt to a known subset — headings, bullet and
 * numbered lists, bold/italic/code, tables, and links. That subset is a couple
 * of hundred lines, and it avoids adding a dependency tree to a CRA build for
 * one panel.
 *
 * XSS: this builds React elements and never touches dangerouslySetInnerHTML, so
 * model output cannot inject markup by construction. Link hrefs are the one
 * place a string reaches the DOM as a URL, so the scheme is allowlisted —
 * blocking `javascript:` and `data:` payloads.
 */

import React from 'react';

// ─── Inline formatting ───────────────────────────────────────────────────────

/** `code`, **bold**, *italic*, and [text](url) — matched in one pass. */
const INLINE_PATTERN = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*\n]+\*)|(\[[^\]]+\]\([^)]+\))/g;

/**
 * Only http(s) and mailto links render as anchors.
 *
 * A model can emit any string it likes inside `](...)`. Without this check a
 * reply containing `javascript:...` would become a clickable script trigger.
 * Anything else falls back to plain text.
 */
function safeHref(url: string): string | null {
  const trimmed = url.trim();
  return /^(https?:\/\/|mailto:|\/)/i.test(trimmed) ? trimmed : null;
}

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;

  // exec() with /g is stateful; the regex is module-level so reset before use.
  INLINE_PATTERN.lastIndex = 0;

  while ((match = INLINE_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    const key = `${keyPrefix}-i${i++}`;

    if (token.startsWith('`')) {
      nodes.push(
        <code key={key} className="px-1 py-0.5 rounded bg-white/15 text-teal-200 text-[0.85em] font-mono">
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith('**')) {
      nodes.push(<strong key={key} className="font-semibold text-white">{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('*')) {
      nodes.push(<em key={key}>{token.slice(1, -1)}</em>);
    } else {
      const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
      const href = linkMatch ? safeHref(linkMatch[2]) : null;
      if (linkMatch && href) {
        nodes.push(
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-teal-300 underline underline-offset-2 hover:text-teal-200"
          >
            {linkMatch[1]}
          </a>,
        );
      } else {
        nodes.push(linkMatch ? linkMatch[1] : token);
      }
    }
    lastIndex = match.index + token.length;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

// ─── Block parsing ───────────────────────────────────────────────────────────

/** Split a `| a | b |` row into trimmed cells. */
function tableCells(line: string): string[] {
  return line.replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
}

/** A `|---|:--:|` separator marks the preceding line as a table header. */
function isTableSeparator(line: string): boolean {
  return /^\|?[\s:-]*-[\s|:-]*\|?$/.test(line.trim()) && line.includes('-');
}

export default function Markdown({ content }: { content: string }) {
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  const blocks: React.ReactNode[] = [];

  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Blank line — paragraph break, nothing to emit.
    if (!trimmed) { i++; continue; }

    // One stable id per block, captured as a const. The callbacks below close
    // over it, so reading the mutable `key` directly would be a stale-capture
    // bug waiting to happen (and trips eslint's no-loop-func).
    const k = key++;

    // ── Fenced code block ────────────────────────────────────────────────
    if (trimmed.startsWith('```')) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        body.push(lines[i]);
        i++;
      }
      i++; // consume the closing fence
      blocks.push(
        <pre
          key={k}
          className="my-2 p-3 rounded-lg bg-black/30 border border-white/10 overflow-x-auto text-xs font-mono text-teal-100"
        >
          <code>{body.join('\n')}</code>
        </pre>,
      );
      continue;
    }

    // ── Table ────────────────────────────────────────────────────────────
    // Requires a header row followed by a separator, which is what the model
    // is asked to produce. A lone `|` line is treated as a paragraph.
    if (trimmed.startsWith('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      const header = tableCells(trimmed);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        rows.push(tableCells(lines[i].trim()));
        i++;
      }
      blocks.push(
        <div key={k} className="my-2 overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr>
                {header.map((h, hi) => (
                  <th
                    key={hi}
                    className="text-left font-semibold text-white/90 px-2 py-1.5 border-b border-white/20 whitespace-nowrap"
                  >
                    {renderInline(h, `th${k}-${hi}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} className="border-b border-white/5">
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-2 py-1.5 text-white/80 align-top">
                      {renderInline(cell, `td${k}-${ri}-${ci}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // ── Heading ──────────────────────────────────────────────────────────
    const heading = /^(#{1,4})\s+(.*)$/.exec(trimmed);
    if (heading) {
      const level = heading[1].length;
      const size = level <= 2 ? 'text-sm' : 'text-[13px]';
      blocks.push(
        <div key={k} className={`${size} font-semibold text-white mt-3 mb-1 first:mt-0`}>
          {renderInline(heading[2], `h${k}`)}
        </div>,
      );
      i++;
      continue;
    }

    // ── Horizontal rule ──────────────────────────────────────────────────
    if (/^(---|\*\*\*|___)$/.test(trimmed)) {
      blocks.push(<hr key={k} className="my-3 border-white/15" />);
      i++;
      continue;
    }

    // ── Lists (bulleted or numbered) ─────────────────────────────────────
    const bullet = /^[-*+]\s+(.*)$/.exec(trimmed);
    const numbered = /^(\d+)[.)]\s+(.*)$/.exec(trimmed);
    if (bullet || numbered) {
      const ordered = !!numbered;
      const items: string[] = [];
      while (i < lines.length) {
        const t = lines[i].trim();
        const b = /^[-*+]\s+(.*)$/.exec(t);
        const n = /^(\d+)[.)]\s+(.*)$/.exec(t);
        if (ordered && n) items.push(n[2]);
        else if (!ordered && b) items.push(b[1]);
        else break;
        i++;
      }
      const ListTag = ordered ? 'ol' : 'ul';
      blocks.push(
        <ListTag
          key={k}
          className={`my-1.5 ml-4 space-y-1 ${ordered ? 'list-decimal' : 'list-disc'} marker:text-teal-400`}
        >
          {items.map((item, ii) => (
            <li key={ii} className="text-white/85 text-[13px] leading-relaxed pl-1">
              {renderInline(item, `li${k}-${ii}`)}
            </li>
          ))}
        </ListTag>,
      );
      continue;
    }

    // ── Paragraph ────────────────────────────────────────────────────────
    // Consecutive non-blank, non-special lines join into one paragraph so a
    // soft-wrapped sentence doesn't render as several stacked lines.
    const para: string[] = [trimmed];
    i++;
    while (i < lines.length) {
      const t = lines[i].trim();
      if (
        !t || t.startsWith('#') || t.startsWith('|') || t.startsWith('```') ||
        /^[-*+]\s/.test(t) || /^\d+[.)]\s/.test(t) || /^(---|\*\*\*|___)$/.test(t)
      ) break;
      para.push(t);
      i++;
    }
    blocks.push(
      <p key={k} className="text-white/85 text-[13px] leading-relaxed my-1.5 first:mt-0">
        {renderInline(para.join(' '), `p${k}`)}
      </p>,
    );
  }

  return <div className="pulsebot-markdown">{blocks}</div>;
}
